import { ENDPOINTS, sendAsyncRequest } from '../utils';
import { PaginatedResponse, UserCreateData, UserUpdateData, ApiResponse, ApiError, ApiErrorCode } from '../types/api.types';
import { User } from '../store/user/types';

/**
 * Extract structured error from axios error response
 */
const extractApiError = (error: unknown): ApiError => {
  if (error && typeof error === 'object') {
    const axiosError = error as {
      response?: {
        data?: {
          error?: {
            code?: ApiErrorCode;
            message?: string;
            field?: string;
            details?: Record<string, unknown>;
          };
          message?: string;
          code?: ApiErrorCode;
          field?: string;
        };
        status?: number;
      };
      message?: string;
    };

    const responseData = axiosError.response?.data;
    const errorData = responseData?.error || responseData;
    const statusCode = axiosError.response?.status;

    if (errorData) {
      return new ApiError(
        errorData.message || axiosError.message || 'Unknown error',
        errorData.code,
        errorData.field,
        statusCode,
        errorData.details
      );
    }

    return new ApiError(
      axiosError.message || 'Unknown error',
      undefined,
      undefined,
      statusCode
    );
  }

  if (error instanceof Error) {
    return new ApiError(error.message);
  }

  return new ApiError('Unknown error');
};

const getUsersList = async (
  page?: number,
  perPage?: number,
  functionAlias?: string,
  userType?: string,
): Promise<PaginatedResponse<User>> => {
  const params: Record<string, unknown> = {
    page,
    limit: perPage,
    filter: { include: [{ relation: 'roles' }], order: ['firstname'] },
  };
  if (functionAlias) {
    params.functionAlias = functionAlias;
  }
  if (userType) {
    params.userType = userType;
  }
  const response = await sendAsyncRequest('get', ENDPOINTS.USERS, true, params, undefined);
  return response.data;
};

const addNewUser = async (data: UserCreateData): Promise<ApiResponse> => {
  try {
    const response = await sendAsyncRequest('post', ENDPOINTS.USERS, true, undefined, data);
    return response;
  } catch (error) {
    throw extractApiError(error);
  }
};

const editUsr = async (
  id: string,
  data: Omit<UserUpdateData, 'id'>,
): Promise<ApiResponse> => {
  try {
    const response = await sendAsyncRequest('put', ENDPOINTS.USERS + '/' + id, true, undefined, data);
    return response;
  } catch (error) {
    throw extractApiError(error);
  }
};

const getRoles = async (): Promise<ApiResponse> => {
  const response = await sendAsyncRequest<ApiResponse>('get', ENDPOINTS.ROLES, true, undefined, undefined);
  return response.data;
};

const duplicatePlanning = async (
  sourceUserId: string,
  data: {
    targetUserId: string;
    overwriteExisting?: boolean;
    mode: 'weekTemplate' | 'realWeek';
    weekDate?: string;
  },
): Promise<ApiResponse> => {
  const response = await sendAsyncRequest(
    'post',
    `${ENDPOINTS.USERS}/${sourceUserId}/duplicate-planning`,
    true,
    undefined,
    data,
  );
  return response.data;
};

export const users = { getUsersList, addNewUser, editUsr, getRoles, duplicatePlanning };
